import React, { useEffect, useState, useCallback } from 'react';
import Carousel from 'react-multi-carousel';
import "react-multi-carousel/lib/styles.css";
import CardRenderer from '../../Utils/CardRenderer';
import "./HomePage.css"
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Card } from '../CardComponent/CardComponent';
import { FaHome, FaBorderAll, FaUsersCog, FaBoxes, FaFire, FaClock, FaStar, FaHeart, FaShare, FaFilter, FaUser, FaChevronRight } from "react-icons/fa";
import { filterPosts } from '../../Utils/Utils';
import CardLoader from '../CardLoading/CardLoading';
import VerticalAd from '../../Utils/VerticalBanner';
import InArticleAd from '../../Utils/AdUnits/InArticleAd';
import InFeedAd from '../../Utils/AdUnits/InFeedAd';
import MultiplexAd from '../../Utils/AdUnits/MultiplexAd';
import { apiUrl } from '../../config/api.js';
import { devLog } from '../../config/devLog.js';
import { isAdSenseHostAllowed } from '../../config/adsense.js';


function HomePage(props) {
  const shouldShowBottomAds = isAdSenseHostAllowed();
  const navigate = useNavigate()
  const [topStories, setTopStories] = useState([])
  const [latestPosts, setLatestPosts] = useState([])
  const [featuredPosts, setFeaturedPosts] = useState([])
  const [topStoriesLoading, setTopStoriesLoading] = useState(true)
  const [latestLoading, setLatestLoading] = useState(true)
  const [featuredLoading, setFeaturedLoading] = useState(true)
  const [activeFilter, setActiveFilter] = useState("0");
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [activeSponsoredIndex, setActiveSponsoredIndex] = useState(0);
  const [showBottomAdSection, setShowBottomAdSection] = useState(shouldShowBottomAds);

  const { userId, userDetails, location } = useSelector((state) => state.userReducer);

  const shareMessage = `Discover Corporate NRI — a smarter way to find rentals, manage utilities, and streamline everyday tasks. Explore curated listings and connect with your community: https://corporatenri.com/`;

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(shareMessage);
    const url = `https://api.whatsapp.com/send?text=${text}`;
    window.open(url, '_blank');
  };

  const handleBottomAdSlotStateChange = useCallback((status) => {
    if (!shouldShowBottomAds) return;
    if (status === "unfilled") {
      setShowBottomAdSection(false);
      return;
    }
    if (status === "filled") {
      setShowBottomAdSection(true);
    }
  }, [shouldShowBottomAds]);
  

    const responsive = {
        desktop: {
          breakpoint: { max: 3000, min: 1024 },
          items: 4,
          slidesToSlide: 2,
        },
        tablet: {
          breakpoint: { max: 1024, min: 464 },
          items: 2,
          slidesToSlide: 2,
        },
        mobile: {
          breakpoint: { max: 464, min: 0 },
          items: 1,
          slidesToSlide: 1,
        },
      };

    useEffect(()=>{
      devLog("user details",userId )
    },[userId])

    useEffect(()=>{
      getFeaturedPosts();
      getDashboardPosts();
      getLatestPosts()
    },[]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      const normalizeAdImageUrl = (value) => {
        const raw = String(value || "").trim();
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        return "";
      };
      const loadSponsoredAds = async () => {
        try {
          const res = await fetch(apiUrl("getSponsoredAds"));
          if (!res.ok) throw new Error("failed");
          const rows = await res.json();
          const normalized = Array.isArray(rows)
            ? rows.map((r) => ({
                id: r.id,
                title: r.title || "",
                subText: r.sub_text || "",
                imageUrl: normalizeAdImageUrl(r.image_url),
                ctaText: r.cta_text || "Learn More",
                ctaUrl: r.cta_url || "",
              }))
            : [];
          setSponsoredAds(normalized);
        } catch (_) {
          setSponsoredAds([]);
        }
      };
      loadSponsoredAds();
    }, []);

    useEffect(() => {
      setActiveSponsoredIndex(0);
    }, [sponsoredAds]);

    useEffect(() => {
      if (sponsoredAds.length <= 1) return;
      const intervalId = window.setInterval(() => {
        setActiveSponsoredIndex((prev) => (prev + 1) % sponsoredAds.length);
      }, 10000);
      return () => window.clearInterval(intervalId);
    }, [sponsoredAds]);

    const handleSponsoredAdClick = (url) => {
      const raw = String(url || "").trim();
      if (!raw) return;
      if (/^https?:\/\//i.test(raw)) {
        window.open(raw, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(raw.startsWith("/") ? raw : `/${raw}`);
    };

    const rotatingSponsoredAds = sponsoredAds.slice(0, 3);
    const activeSponsoredAd =
      rotatingSponsoredAds.length > 0
        ? rotatingSponsoredAds[activeSponsoredIndex % rotatingSponsoredAds.length]
        : null;

    const updateSelectedCategory = (newValue) =>{
      getFeaturedPosts(newValue);
      getDashboardPosts(newValue);
      getLatestPosts(newValue)
      setActiveFilter(newValue)
    }

    // Enhanced filter categories with better UX
    const filterCategories = [
      { id: "0", name: "All", icon: FaBorderAll, color: "#667eea", count: topStories.length },
      { id: "1", name: "Houses", icon: FaHome, color: "#f093fb", count: topStories.filter(p => p.category === "1").length },
      { id: "2", name: "Utility", icon: FaBoxes, color: "#4facfe", count: topStories.filter(p => p.category === "2").length },
      { id: "3", name: "Others", icon: FaUsersCog, color: "#43e97b", count: topStories.filter(p => p.category === "3").length }
    ];

  const sortByLocationAndDate = useCallback((data, locationArg = userDetails.preferred_location.split("_").length > 1 ? userDetails.preferred_location.split("_")[1]?.toLowerCase() : "") => {
    if (!Array.isArray(data)) {
      return data || [];
    }
    if (!locationArg || locationArg.length === 0) {
      return data;
    }
    const loc = String(locationArg).toLowerCase();
    return [...data].sort((a, b) => {
      const aLoc = (a.location && String(a.location).toLowerCase()) || "";
      const bLoc = (b.location && String(b.location).toLowerCase()) || "";
      if (aLoc === loc && bLoc !== loc) return -1;
      if (bLoc === loc && aLoc !== loc) return 1;
      return new Date(b.createdDate) - new Date(a.createdDate);
    });
  }, [userDetails.preferred_location]);

    useEffect(()=>{
      devLog('location',location)
      setFeaturedPosts(prev => sortByLocationAndDate(prev, location.toLowerCase()))
      setTopStories(prev => sortByLocationAndDate(prev, location.toLowerCase()))
      setLatestPosts(prev => sortByLocationAndDate(prev, location.toLowerCase()))
    },[location, sortByLocationAndDate])

  const getFeaturedPosts = async(filter=activeFilter)=>{
      devLog("entered get today posts api")
      let response = await fetch(apiUrl("getFeaturedPosts?"+filter))
      const postResponse = await response.json();
      const sotedPosts = sortByLocationAndDate(postResponse,location.toLowerCase())

      const filteredPosts = await filterPosts(sotedPosts)
      setFeaturedPosts(filteredPosts.splice(0,3));
      setFeaturedLoading(false);
    }

  const getDashboardPosts = async(filter=activeFilter)=>{
    devLog("entered get today posts api")
    let response = await fetch(apiUrl("getDashboardPosts?"+filter))
    const postResponse = await response.json();
    const sotedPosts = sortByLocationAndDate(postResponse,location.toLowerCase())

    
    devLog(postResponse)

    const filteredPosts = await filterPosts(sotedPosts)
    devLog(filteredPosts)
    setTopStories(filteredPosts);
    setTopStoriesLoading(false);

  }


  const getLatestPosts = async(filter=activeFilter)=>{
    devLog("entered get today posts api")
    let response = await fetch(apiUrl("getLatestPosts?"+filter))
    const postResponse = await response.json();
    
    // devLog(postResponse)
    const sotedPosts = sortByLocationAndDate(postResponse,location.toLowerCase())

    const filteredPosts = await filterPosts(sotedPosts)
    devLog("latest posts",filteredPosts)
    setLatestPosts(filteredPosts);
    setLatestLoading(false);
  }



    return (
      <div className="homepage-container">

        {/* Enhanced Smart Filter Bar - Sticky Top */}
        <div className="smart-filter-bar">
          <div className="filter-container">
            <div className="filter-left">
              <div className="filter-title-section">
                 <FaFilter className="filter-main-icon" />
                {/*<h3>Filters</h3>
                <span className="filter-subtitle">Find exactly what you need</span> */}
              </div>
            </div>

            
            
            <div className="filter-center">
              <div className="quick-filters">
                {filterCategories.map((category) => {
                  const IconComponent = category.icon;
                  return (
                    <button 
                      key={category.id}
                      className={`quick-filter-btn ${activeFilter === category.id ? 'active' : ''}`}
                      onClick={() => updateSelectedCategory(category.id)}
                      style={{ '--category-color': category.color }}
                    >
                      <IconComponent className="filter-icon" />
                      <span className="filter-name">{category.name}</span>
                     {/* <span className="filter-count">({category.count})</span> */}
                    </button>
                  );
                })}
              </div>
            </div>
            
          </div>
        </div>

        {/* Top Stories Section */}
        <div className="top-stories-section">
          <div className="section-header">
            <div className="section-title">
              <FaFire className="title-icon" />
              <h2>Trending Now</h2>
            </div>
            <button className="view-all-btn" onClick={() => navigate("/trending")}>
              View All <FaChevronRight />
            </button>
          </div>
          
          <div className="carousel-container">
            {!topStoriesLoading && topStories.length === 0 ? (
              <p className="carousel-empty-message" style={{ padding: "1.5rem", textAlign: "center", color: "#64748b" }}>
                No trending posts in this category yet.
              </p>
            ) : (
            <Carousel
              additionalTransfrom={0}
              arrows
              autoPlay={topStoriesLoading || topStories.length > 1}
              autoPlaySpeed={4000}
              centerMode={false}
              className="trending-carousel"
              containerClass="container-with-dots"
              dotListClass="custom-dots"
              draggable
              focusOnSelect={false}
              infinite={false}
              itemClass="carousel-item"
              keyBoardControl
              minimumTouchDrag={80}
              pauseOnHover
              renderArrowsWhenDisabled={false}
              renderButtonGroupOutside={false}
              renderDotsOutside={true}
              rewind
              rewindWithAnimation={false}
              rtl={false}
              shouldResetAutoplay
              showDots={!topStoriesLoading && topStories.length > 1}
              sliderClass="slider"
              swipeable
              responsive={responsive}
            >
              {topStoriesLoading ? 
                [1,2,3,4].map((item) => (
                  <div key={item} className="trending-card-skeleton">
                    <div className="skeleton-image"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-location"></div>
                    </div>
                  </div>
                )) :
                topStories.map((story, index) => (
                  <div key={story.id} className="trending-card">
                    <div className="trending-rank">#{index + 1}</div>
                    <Card 
                      title={story.title}
                      location={story.location}
                      image={story.images[0]} 
                      navigateURL={"/trending?id=" + story.id}
                    />
                  </div>
                ))
              }
            </Carousel>
            )}
          </div>
        </div>

        {/* Main Content Grid with Strategic Ad Placement */}
        <div className="main-content-grid">
          {/* Left Column - Latest Posts */}
          <div className="content-column latest-posts-column">
            <div className="section-card">
              <div className="section-header">
                <div className="section-title">
                  <FaClock className="title-icon" />
                  <h2>Latest Posts</h2>
                  <span className="new-badge">NEW</span>
                </div>
                <button className="view-all-btn" onClick={() => navigate("/latest")}>
                  View All <FaChevronRight />
                </button>
              </div>
              
              <div className="posts-container">
                {latestLoading ? (
                  <>
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </>
                ) : (
                  latestPosts.map((post, idx) => (
                    <React.Fragment key={post.id}>
                      <CardRenderer
                        post={{
                          id: post.id,
                          title: post.title,
                          price: post.price,
                          location: post.location,
                          category: post.category,
                          amenities: post.amenities,
                          postedBy: post.postedBy,
                          createdAt: post.createdAt,
                          image: post.images[0]
                        }}
                        component="latest"
                      />
                      {idx === 1 && <InFeedAd />}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Featured Posts + Ads */}
          <div className="content-column featured-posts-column">
            {/* Featured Posts */}
            <div className="section-card">
              <div className="section-header">
                <div className="section-title">
                  <FaStar className="title-icon" />
                  <h2>Featured Posts</h2>
                </div>
                <button className="view-all-btn" onClick={() => navigate("/featured")}>
                  View All <FaChevronRight />
                </button>
              </div>
              
              <div className="posts-container">
                {featuredLoading ? (
                  <>
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </>
                ) : (
                  featuredPosts.map((post) => (
                    <CardRenderer 
                      key={post.id}
                      post={{
                        id: post.id, 
                        title: post.title, 
                        price: post.price, 
                        location: post.location, 
                        category: post.category, 
                        amenities: post.amenities, 
                        postedBy: post.postedBy, 
                        createdAt: post.createdAt, 
                        image: post.images[0] 
                      }} 
                      component="featured"
                    />
                  ))
                )}
              </div>
              <InArticleAd />
            </div>

            {/* Strategic Ad Placement */}
            <div className="ad-section">
              <div className="ad-container">
                <div className="ad-label">Sponsored</div>
                <div className="manual-sponsored-list">
                  {activeSponsoredAd ? (
                      <button
                        key={activeSponsoredAd.id}
                        type="button"
                        className="manual-sponsored-card"
                        onClick={() => handleSponsoredAdClick(activeSponsoredAd.ctaUrl)}
                      >
                        {activeSponsoredAd.imageUrl ? (
                          <img
                            src={activeSponsoredAd.imageUrl}
                            alt={activeSponsoredAd.title || "Sponsored ad"}
                            className="manual-sponsored-image"
                          />
                        ) : null}
                        <h4>{activeSponsoredAd.title || "Sponsored"}</h4>
                        <p>{activeSponsoredAd.subText || ""}</p>
                        <span>{activeSponsoredAd.ctaText || "Learn More"}</span>
                      </button>
                  ) : (
                    <div className="manual-sponsored-empty">No sponsored ads right now.</div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Vertical Ad Space */}
            <div className="vertical-ad-section">
              <VerticalAd />
            </div>
          </div>
        </div>

        {/* Bottom Engagement Section */}
        {shouldShowBottomAds && showBottomAdSection && (
          <div className="ad-section">
            <div className="ad-container">
              <div className="ad-label">Advertisements for you</div>
              <MultiplexAd onSlotStateChange={handleBottomAdSlotStateChange} />
            </div>
          </div>
        )}

        <div className="engagement-section">
          <div className="engagement-content">
            <h3>Stay Connected with Your Community</h3>
            <div className="engagement-actions">
              <button className="engagement-btn primary" onClick={() => navigate("/latest")}>
                <FaHeart />
                View Posts
              </button>
              <button className="engagement-btn secondary" onClick={handleWhatsAppShare}>
                <FaShare />
                Share Community
              </button>
              <button className="engagement-btn tertiary" onClick={handleWhatsAppShare}>
                <FaUser />
                Invite Friends
              </button>
            </div>
          </div>
        </div>
      </div>
    );
}

export default HomePage;